import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Route, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const Dot = ({ ok }) => <span className={`inline-block w-2.5 h-2.5 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "-");

const QuotaRow = ({ label, q, testId }) => {
  const pct = q?.limit ? Math.round((q.remaining / q.limit) * 100) : null;
  return (
    <div className="text-xs" data-testid={testId}>
      <div className="flex justify-between">
        <span className="text-zinc-600">{label}</span>
        <span className="font-semibold">{q ? `${q.remaining} / ${q.limit ?? "?"} tersisa` : "belum ada data"}</span>
      </div>
      {pct !== null && (
        <div className="h-1.5 bg-zinc-200 rounded mt-1 overflow-hidden">
          <div className={`h-full ${pct > 30 ? "bg-emerald-500" : pct > 10 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {q?.reset_at && <div className="text-[10px] text-zinc-400 mt-0.5">Reset: {fmt(q.reset_at)}</div>}
    </div>
  );
};

export const RoutingEngineStatus = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (checkOrs = false) => {
    setBusy(true);
    try { setSt(await api.routingStatus(checkOrs)); if (checkOrs) toast.success("Status mesin rute diperbarui"); }
    catch { toast.error("Gagal memuat status mesin rute"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(false); }, [load]);

  const toggle = async (name, on) => {
    try {
      const { enabled } = await api.routingEnginesPut({ [name]: on });
      setSt((p) => ({ ...p, enabled }));
      toast.success(`${name === "osrm" ? "OSRM" : "OpenRouteService"} ${on ? "ON" : "OFF"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };
  const en = st?.enabled || { osrm: true, ors: true };
  const engineSwitch = (name) => (
    <label className="ml-auto flex items-center gap-1.5 text-[11px] font-semibold">
      <span className={en[name] ? "text-emerald-700" : "text-zinc-400"}>{en[name] ? "ON" : "OFF"}</span>
      <Switch checked={en[name]} onCheckedChange={(v) => toggle(name, v)} data-testid={`routing-${name}-toggle`} />
    </label>
  );
  const orderText = en.osrm && en.ors
    ? "Penjadwalan: OSRM → OpenRouteService → garis lurus. Rute Menuju Lokasi: OpenRouteService → OSRM → garis lurus."
    : en.osrm ? "Semua perhitungan rute: OSRM → garis lurus (ORS OFF)."
    : en.ors ? "Semua perhitungan rute: OpenRouteService → garis lurus (OSRM OFF)."
    : "Kedua mesin OFF — semua perhitungan memakai garis lurus.";

  const orsOk = st?.ors?.configured && (st.ors.live ? st.ors.live.ok : st.ors.quota?.directions?.last_status !== 429);
  const used = Object.entries(st?.tracking_routes_24h || {});

  return (
    <div className="border border-border rounded-sm bg-white p-5" data-testid="routing-status-section">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Route className="w-5 h-5 text-blue-700" />
        <h2 className="text-base font-bold tracking-tight">Status Mesin Rute</h2>
        <Button size="sm" variant="outline" className="ml-auto h-7" disabled={busy} onClick={() => load(true)} data-testid="routing-status-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Cek sekarang
        </Button>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">
        <span data-testid="routing-order-text">{orderText}</span> "Cek sekarang" memakai 1 kuota ORS.
      </p>
      {!st ? <Loader2 className="w-4 h-4 animate-spin text-zinc-400" /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="border rounded-sm p-3 space-y-1" data-testid="routing-osrm-card">
            <div className="flex items-center gap-2 font-semibold text-sm"><Dot ok={st.osrm.ok && en.osrm} /> OSRM (server sendiri){engineSwitch("osrm")}</div>
            <div className="text-xs text-zinc-600 break-all">{st.osrm.url || "OSRM_URL belum di-set"}</div>
            <div className="text-xs" data-testid="routing-osrm-status">
              {st.osrm.ok ? `Aktif · respons ${st.osrm.latency_ms} ms` : `Tidak aktif · ${st.osrm.error}`}
            </div>
          </div>
          <div className="border rounded-sm p-3 space-y-2" data-testid="routing-ors-card">
            <div className="flex items-center gap-2 font-semibold text-sm"><Dot ok={orsOk && en.ors} /> OpenRouteService{engineSwitch("ors")}</div>
            <div className="text-xs" data-testid="routing-ors-status">
              {!st.ors.configured ? "ORS_API_KEY belum di-set"
                : st.ors.live ? (st.ors.live.ok ? "Aktif · key valid" : `Error · ${st.ors.live.error}`)
                : "Key terpasang · tekan Cek sekarang untuk uji langsung"}
            </div>
            <QuotaRow label="Kuota Directions (rute) hari ini" q={st.ors.quota?.directions} testId="routing-ors-quota-directions" />
            <QuotaRow label="Kuota Matrix hari ini" q={st.ors.quota?.matrix} testId="routing-ors-quota-matrix" />
            {st.ors.quota?.directions?.updated_at && <div className="text-[10px] text-zinc-400">Data kuota: {fmt(st.ors.quota.directions.updated_at)}</div>}
          </div>
          <div className="sm:col-span-2 text-xs text-zinc-600" data-testid="routing-usage-24h">
            Rute Menuju Lokasi 24 jam terakhir: {used.length ? used.map(([k, n]) => `${k.toUpperCase()} ${n}`).join(" · ") : "belum ada"}
          </div>
        </div>
      )}
    </div>
  );
};
